import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { ItemMediaList } from '@euler/app/flows/task/components/ItemMediaList';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useShakeEffect } from '@euler/functions/useShakeEffect';
import {
  DeliveryCheckItemStaged,
  DeliveryCheckItemStagedOption,
} from '@euler/model/task-detail';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import {
  LayoutChangeEvent,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  SlideInLeft,
  SlideInRight,
  ZoomOutRotate,
} from 'react-native-reanimated';

export type DeliveryCheckCellRef = {
  shake(callback?: () => void): void;
};

type Props = {
  mode: 'wizard' | 'list';
  showErrorIndicators?: boolean;
  compact?: boolean;
  item: DeliveryCheckItemStaged;
  index: number;
  prevIndex?: number;
  total: number;
  scrollable?: boolean;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  optionListStyle?: StyleProp<ViewStyle>;
  mediaRemarkContainerStyle?: StyleProp<ViewStyle>;
  remarkStyle?: StyleProp<TextStyle>;
  alwaysShowControls?: boolean;
  removeButtonPlacement?: 'outside' | 'inside';
  removeButtonSize?: number;
  onEditUnacceptedReason?: (key: string) => void;
  onEditItemReworkRecord?: (key: string) => void;
  onRemoveItemReworkRecord?: (key: string) => void;
  onTakeMedia?: (key: string, allowChooseMediaFromGallery: boolean) => void;
  onDeleteMedia?: (key: string, media: MediaInfo) => void;
  onEditMedia?: (key: string, media: MediaInfo) => void;
  onRetryMediaUpload?: (key: string, media: MediaInfo) => void;
  onPreviewVideo?: (key: string, media: MediaInfo) => void;
  onRemarkChange?: (key: string, text: string) => void;
  onOptionCheckChange?: (
    key: string,
    option: DeliveryCheckItemStagedOption,
    index: number,
  ) => void;
  onLayout?: (key: string, e: LayoutChangeEvent) => void;
  onRemove?: (key: string) => void;
};

export const DeliveryCheckCell = memo(
  forwardRef<DeliveryCheckCellRef, Props>(
    (
      {
        mode,
        item,
        compact,
        scrollable,
        alwaysShowControls,
        showErrorIndicators,
        style,
        titleStyle,
        optionListStyle,
        mediaRemarkContainerStyle,
        remarkStyle,
        removeButtonSize,
        removeButtonPlacement = 'inside',
        index,
        prevIndex,
        total,
        onEditUnacceptedReason,
        onEditItemReworkRecord,
        onRemoveItemReworkRecord,
        onTakeMedia,
        onDeleteMedia,
        onEditMedia,
        onRetryMediaUpload,
        onPreviewVideo,
        onRemarkChange,
        onOptionCheckChange,
        onLayout,
        onRemove,
      },
      ref,
    ) => {
      const { rpx } = useResponsiveContext();
      const { shake, shakeStyle } = useShakeEffect();

      removeButtonSize = removeButtonSize ?? rpx(32);

      useImperativeHandle(ref, () => ({
        shake,
      }));

      const medias = useMemo(
        () => item.medias?.map(mediaStagedToMediaInfo) ?? [],
        [item.medias],
      );

      const handleTakeMedia = useCallback(() => {
        onTakeMedia?.(item._key, item.allowChooseMediaFromGallery !== false);
      }, [onTakeMedia, item._key, item.allowChooseMediaFromGallery]);

      const handleDeleteMedia = useCallback(
        (media: MediaInfo) => {
          onDeleteMedia?.(item._key, media);
        },
        [item._key, onDeleteMedia],
      );

      const handleEditMedia = useCallback(
        (media: MediaInfo) => {
          onEditMedia?.(item._key, media);
        },
        [onEditMedia, item._key],
      );

      const handleRetryMediaUpload = useCallback(
        (media: MediaInfo) => {
          onRetryMediaUpload?.(item._key, media);
        },
        [onRetryMediaUpload, item._key],
      );

      const handlePreviewVideo = useCallback(
        (media: MediaInfo) => {
          onPreviewVideo?.(item._key, media);
        },
        [onPreviewVideo, item._key],
      );

      const handleRemarkTextChange = useCallback(
        (text: string) => {
          onRemarkChange?.(item._key, text);
        },
        [item._key, onRemarkChange],
      );

      const handleEditUnacceptedReason = useCallback(() => {
        onEditUnacceptedReason?.(item._key);
      }, [item._key, onEditUnacceptedReason]);

      const handleEditItemReworkRecord = useCallback(() => {
        onEditItemReworkRecord?.(item._key);
      }, [item._key, onEditItemReworkRecord]);

      const handleRemoveItemReworkRecord = useCallback(() => {
        onRemoveItemReworkRecord?.(item._key);
      }, [item._key, onRemoveItemReworkRecord]);

      const handleLayoutEvent = useCallback(
        (e: LayoutChangeEvent) => {
          onLayout?.(item._key, e);
        },
        [item._key, onLayout],
      );

      const handleRemove = useCallback(() => {
        if (item.removable === false) return;
        onRemove?.(item._key);
      }, [item._key, item.removable, onRemove]);

      const showDetailsSection = Boolean(
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        alwaysShowControls ||
          item.reworkRecords.length ||
          item.selectedOptionIndex != null ||
          medias.length > 0 ||
          mode === 'wizard',
      );

      const onOptionPress = useCallback(
        (option: DeliveryCheckItemStagedOption, k: number) => {
          if (item.selectedOptionIndex !== k) {
            onOptionCheckChange?.(item._key, option, k);
          }
        },
        [item._key, item.selectedOptionIndex, onOptionCheckChange],
      );

      const ContentContainer = scrollable ? ScrollView : View;

      return (
        <Animated.View
          key={item._key}
          //layout={Layout.springify()}
          exiting={mode === 'wizard' ? ZoomOutRotate : undefined}
          entering={
            mode === 'wizard'
              ? prevIndex != null && index < prevIndex
                ? SlideInLeft.duration(350)
                : SlideInRight.duration(350).delay(
                    mode === 'wizard' ? 0 : index * 100,
                  )
              : undefined
          }
          css={`
            background-color: #fff;
            border-radius: ${rpx(16)}px;
            padding: ${rpx(28)}px ${rpx(20)}px ${rpx(20)}px ${rpx(20)}px;
            box-shadow: 0 0 ${rpx(5)}px rgba(0, 0, 0, 0.1);
            border-width: 1px;
            border-color: ${showErrorIndicators ? StatusColors.Danger : '#fff'};
          `}
          style={[shakeStyle, style]}
          onLayout={handleLayoutEvent}
        >
          <ContentContainer
            style={{ flex: 1 }}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
          >
            <StackLayout
              justifyContent="flex-start"
              alignItems="center"
              direction="row"
              gap={rpx(5)}
              //backgroundColor={'green'}
            >
              <Text
                css={`
                  font-weight: 700;
                  font-style: italic;
                  font-size: ${rpx(26)}px;
                  color: #207fe7;
                  line-height: ${rpx(26)}px;
                `}
              >
                {`${index + 1}${mode === 'list' ? '.' : ''}`}
              </Text>
              {mode === 'wizard' && (
                <Text
                  css={`
                    font-weight: 400;
                    font-style: italic;
                    font-size: ${rpx(10)}px;
                    color: #666;
                    line-height: ${rpx(32)}px;
                  `}
                >
                  {`/${total} `}
                </Text>
              )}
              <Typography.Label
                color="#000"
                numberOfLines={0}
                size={rpx(compact ? 18 : 20)}
                medium
                css={`
                  flex: 1;
                  margin-left: ${rpx(8)}px;
                  top: ${mode === 'wizard' ? 0 : rpx(-1)}px;
                  letter-spacing: ${compact ? rpx(-0.75) : 0}px;
                `}
                style={titleStyle}
              >
                {`${item.title.replace(/[?？]+$/, '')}?`}
              </Typography.Label>
              <StackLayout
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={rpx(5)}
              >
                <Center.Touchable
                  onPress={handleEditItemReworkRecord}
                  css={`
                    width: ${rpx(25)}px;
                    height: ${rpx(25)}px;
                    border-radius: ${rpx(25)}px;
                    background-color: rgba(255, 145, 0, 0.31);
                  `}
                >
                  <Typography.Text size={rpx(12)}>返</Typography.Text>
                </Center.Touchable>
                {item.constructionJobId == null &&
                  removeButtonPlacement === 'inside' && (
                    <Center.Touchable
                      onPress={handleRemove}
                      disabled={item.removable === false}
                      css={`
                        width: ${rpx(25)}px;
                        height: ${rpx(25)}px;
                        border-radius: ${rpx(25)}px;
                        background-color: ${item.removable === false
                          ? '#eee'
                          : 'rgb(253, 235, 237)'};
                      `}
                    >
                      <Icon
                        name="trash-o"
                        size={rpx(16)}
                        color={item.removable === false ? '#ccc' : '#f44336'}
                      />
                    </Center.Touchable>
                  )}
              </StackLayout>
            </StackLayout>
            <View
              css={`
                padding: ${rpx(compact ? 10 : 15)}px 0;
                flex-direction: ${compact ? 'row' : 'column'};
                align-items: ${compact ? 'center' : 'flex-start'};
                justify-content: ${compact ? 'space-between' : 'flex-start'};
              `}
              style={optionListStyle}
            >
              {item.options.map((option, k) => (
                <TouchableOpacity
                  key={k}
                  css={`
                    flex-direction: row;
                    align-items: center;
                    margin-top: ${rpx(compact ? 0 : 10)}px;
                    margin-bottom: ${rpx(compact ? 0 : 10)}px;
                    background-color: ${compact
                      ? item.selectedOptionIndex == k && option.isExpected
                        ? 'rgba(0, 170, 85, 0.1)'
                        : item.selectedOptionIndex === k && !option.isExpected
                        ? 'rgba(224, 70, 61, 0.1)'
                        : 'rgba(128, 128, 128, 0.1)'
                      : '#fff'};
                    margin-left: ${rpx(compact && k > 0 ? 10 : 0)}px;
                    border-radius: ${rpx(5)}px;
                    padding: ${rpx(compact ? 8 : 0)}px
                      ${rpx(compact ? 10 : 0)}px;
                  `}
                  style={compact ? { flex: 1 } : undefined}
                  onPress={() => onOptionPress(option, k)}
                >
                  <Icon
                    name={
                      item.selectedOptionIndex === k
                        ? 'radio_button_on'
                        : 'radio_button_off'
                    }
                    size={rpx(22)}
                    color={
                      item.selectedOptionIndex == k && option.isExpected
                        ? 'rgba(0, 170, 85, 1)'
                        : item.selectedOptionIndex === k && !option.isExpected
                        ? 'rgba(224, 70, 61, 1)'
                        : 'rgba(128, 128, 128, 1)'
                    }
                  />
                  <Typography.Label
                    size={rpx(compact ? 14 : 18)}
                    color={
                      item.selectedOptionIndex == k && option.isExpected
                        ? StatusColors.Success
                        : item.selectedOptionIndex === k && !option.isExpected
                        ? 'rgba(224, 70, 61, 1)'
                        : '#000'
                    }
                    family={
                      item.selectedOptionIndex === k ? 'medium' : 'regular'
                    }
                    css={`
                      margin-left: ${rpx(5)}px;
                      margin-right: ${rpx(5)}px;
                    `}
                  >
                    {option.isExpected && option.title === '是'
                      ? '检验合格'
                      : !option.isExpected && option.title === '否'
                      ? '不合格'
                      : option.title}
                  </Typography.Label>
                </TouchableOpacity>
              ))}
            </View>
            {item.unacceptedReason != null && (
              <StackLayout
                direction="row"
                alignItems="center"
                gap={rpx(5)}
                mb={rpx(compact ? 5 : 15)}
                mt={rpx(compact ? 0 : -10)}
              >
                <Icon
                  name="information-outline"
                  size={rpx(14)}
                  color={StatusColors.Warn}
                />
                <Typography.Text
                  color={StatusColors.Warn}
                  size={rpx(14)}
                  lineHeight={18}
                  flex={1}
                >
                  {item.unacceptedReason}
                </Typography.Text>
                <TouchableOpacity onPress={handleEditUnacceptedReason}>
                  <Icon name="note" size={rpx(14)} />
                </TouchableOpacity>
              </StackLayout>
            )}
            {showDetailsSection && (
              <View
                css={`
                  padding: ${rpx(5)}px 0px ${rpx(15)}px 0px;
                `}
                style={mediaRemarkContainerStyle}
              >
                <Typography.Label
                  regular
                  css={`
                    margin-bottom: ${rpx(10)}px;
                  `}
                >
                  <Typography.Label medium color="#555">
                    {'拍照或录制视频 '}
                  </Typography.Label>
                  {item.requiresPhoto ? (
                    <Typography.Label color={StatusColors.Danger}>
                      (*必须)
                    </Typography.Label>
                  ) : (
                    <Typography.Label color="#aaa">(可选): </Typography.Label>
                  )}
                </Typography.Label>
                <View>
                  <ItemMediaList
                    medias={medias}
                    columns={4}
                    editable
                    onTakeMedia={handleTakeMedia}
                    onDeleteMedia={handleDeleteMedia}
                    onEditMedia={handleEditMedia}
                    onPreviewVideo={handlePreviewVideo}
                    onRetryMediaUpload={handleRetryMediaUpload}
                  />
                </View>
                {item.reworkRecords?.length ? (
                  <View>
                    <StackLayout
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={rpx(12)}
                      gap={rpx(5)}
                    >
                      <Typography.Label medium color="#555" flex={1}>
                        内返记录:
                      </Typography.Label>
                      <TouchableOpacity onPress={handleRemoveItemReworkRecord}>
                        <Icon name="trash-o" size={rpx(14)} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={handleEditItemReworkRecord}>
                        <Icon name="note" size={rpx(14)} />
                      </TouchableOpacity>
                    </StackLayout>
                    <View
                      css={`
                        height: ${StyleSheet.hairlineWidth}px;
                        background-color: #ddd;
                        margin: ${rpx(4)}px 0;
                      `}
                    />
                    <StackLayout
                      direction="row"
                      mt={rpx(4)}
                      mb={rpx(8)}
                      gap={rpx(6)}
                    >
                      <Typography.Text color="#555">
                        责任人: {item.reworkRecords[0].responsiblePersonName},
                        备注: {item.reworkRecords[0].remarks?.trim() || '(无)'}
                      </Typography.Text>
                    </StackLayout>
                    <ItemMediaList
                      medias={item.reworkRecords
                        .flatMap(x => x.medias)
                        .map(mediaStagedToMediaInfo)}
                      columns={4}
                      editable={false}
                      onPreviewVideo={handlePreviewVideo}
                    />
                  </View>
                ) : null}
                <Typography.Label mt={rpx(12)} mb={rpx(5)} medium color="#555">
                  {'其他备注信息:'}
                </Typography.Label>
                <TextInput
                  textAlignVertical="top"
                  multiline
                  placeholder="输入备注信息"
                  placeholderTextColor={'#aaa'}
                  style={remarkStyle}
                  clearButtonMode="always"
                  {...(Platform.OS === 'web'
                    ? {
                        value: item.remark || item.defaultRemark || '',
                        onChangeText: handleRemarkTextChange,
                      }
                    : {
                        // note(eric): fix a bug where the multiline text input's
                        // on change event will be fired automatically when the
                        // value prop is changed w/o user interaction.
                        defaultValue: item.remark || item.defaultRemark || '',
                        onEndEditing: e => {
                          handleRemarkTextChange(e.nativeEvent.text);
                        },
                      })}
                  css={`
                    padding: ${rpx(8)}px ${rpx(10)}px;
                    line-height: ${rpx(22)}px;
                    font-size: ${rpx(compact ? 16 : 18)}px;
                    height: ${rpx(60)}px;
                    border-color: #ddd;
                    border-width: 1px;
                    border-radius: ${rpx(10)}px;
                    font-family: ${FontFamily.NotoSans.Regular};
                  `}
                />
              </View>
            )}
          </ContentContainer>
          {item.constructionJobId == null &&
          item.removable !== false &&
          removeButtonPlacement === 'outside' ? (
            <TouchableOpacity
              hitSlop={PredefinedHitSlops.large}
              css={`
                background-color: ${StatusColors.Danger};
                width: ${removeButtonSize}px;
                height: ${removeButtonSize}px;
                border-radius: ${removeButtonSize / 2}px;
                position: absolute;
                top: ${-removeButtonSize / 2}px;
                right: ${-removeButtonSize / 2}px;
                align-items: center;
                justify-content: center;
                z-index: 10;
              `}
              onPress={handleRemove}
            >
              <Icon name="clear" color="#fff" size={removeButtonSize * 0.7} />
            </TouchableOpacity>
          ) : null}
        </Animated.View>
      );
    },
  ),
);
